/**
 * Thin fetch wrapper around the BackOffice API.
 *
 * Responsibilities: attach the bearer token, turn the server's error shapes
 * into one Error type the UI can render, and notify the app when a token stops
 * being accepted so it can bounce the user back to the sign-in screen.
 */

const BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? "/api").replace(/\/$/, "");

const TOKEN_KEY = "backoffice.token";
const OPERATOR_KEY = "backoffice.operator";

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }

  /** 409 — someone else changed the record first (optimistic concurrency),
   * or a ticket number already exists. */
  get isConflict(): boolean {
    return this.status === 409;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }
}

// -- token storage -----------------------------------------------------------
//
// localStorage rather than sessionStorage so a reload or a second tab doesn't
// force a fresh sign-in. The server's own 12-hour TTL still applies, and
// restarting the API invalidates every token regardless of what's stored here.

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setSession(token: string, operator: unknown): void {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(OPERATOR_KEY, JSON.stringify(operator));
}

export function getStoredOperator<T>(): T | null {
  const raw = localStorage.getItem(OPERATOR_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export function clearSession(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(OPERATOR_KEY);
}

/** Fired when the server rejects our token, so the app can show the login
 * screen without every caller having to handle 401 itself. */
const unauthorizedListeners = new Set<() => void>();

export function onUnauthorized(listener: () => void): () => void {
  unauthorizedListeners.add(listener);
  return () => unauthorizedListeners.delete(listener);
}

// -- error decoding ----------------------------------------------------------

interface ValidationDetail {
  loc?: (string | number)[];
  msg?: string;
}

/**
 * The API answers with `{detail: string}` for domain errors (see the
 * BackOfficeError handler in backend/api.py) and `{detail: [...]}` for
 * FastAPI's own request validation. Flatten both into one readable sentence.
 */
async function decodeError(response: Response): Promise<string> {
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return response.statusText || `Request failed (${response.status})`;
  }
  const detail = (body as { detail?: unknown } | null)?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const parts = (detail as ValidationDetail[]).map((item) => {
      const field = item.loc?.filter((part) => part !== "body").join(".");
      return field ? `${field}: ${item.msg ?? "invalid"}` : (item.msg ?? "invalid");
    });
    if (parts.length > 0) return parts.join("; ");
  }
  return response.statusText || `Request failed (${response.status})`;
}

// -- request core ------------------------------------------------------------

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Skip the bearer header — only /auth/login and /health need this. */
  anonymous?: boolean;
  signal?: AbortSignal;
}

async function send(path: string, options: RequestOptions = {}): Promise<Response> {
  const { method = "GET", body, anonymous = false, signal } = options;
  const headers: Record<string, string> = {};
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (!anonymous) {
    const token = getToken();
    if (token) headers["Authorization"] = `Bearer ${token}`;
  }

  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === "AbortError") throw cause;
    // fetch only rejects for transport-level problems — the server being
    // unreachable, DNS failing, or a CORS preflight being refused.
    throw new ApiError(
      "Could not reach the server. Check your connection and that the API is running.",
      0,
    );
  }

  if (response.status === 401 && !anonymous) {
    clearSession();
    unauthorizedListeners.forEach((listener) => listener());
  }
  if (!response.ok) {
    throw new ApiError(await decodeError(response), response.status);
  }
  return response;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await send(path, options);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => apiRequest<T>(path, { signal }),
  post: <T>(path: string, body?: unknown, signal?: AbortSignal) =>
    apiRequest<T>(path, { method: "POST", body, signal }),
  put: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: "PUT", body }),
  patch: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: "PATCH", body }),
  delete: <T>(path: string) => apiRequest<T>(path, { method: "DELETE" }),
  postAnonymous: <T>(path: string, body?: unknown) =>
    apiRequest<T>(path, { method: "POST", body, anonymous: true }),
};

// -- file download -----------------------------------------------------------

export interface DownloadResult {
  filename: string;
  /** The exporter's row count, reported via the X-Row-Count header. */
  rowCount: number | null;
}

function filenameFrom(disposition: string | null, fallback: string): string {
  if (!disposition) return fallback;
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (utf8?.[1]) return decodeURIComponent(utf8[1]);
  const plain = /filename="?([^";]+)"?/i.exec(disposition);
  return plain?.[1] ?? fallback;
}

/**
 * POST a request that answers with a file and hand it to the browser.
 *
 * Done with fetch rather than a plain link because the endpoint needs the
 * Authorization header — a `window.open` on a protected URL would come back
 * 401. The blob URL is revoked once the click has been dispatched.
 */
export async function downloadFile(
  path: string,
  body: unknown,
  fallbackName: string,
): Promise<DownloadResult> {
  const response = await send(path, { method: "POST", body });
  const blob = await response.blob();
  const filename = filenameFrom(response.headers.get("Content-Disposition"), fallbackName);
  const rowCountHeader = response.headers.get("X-Row-Count");

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Revoking synchronously can cancel the download in Safari; one turn of the
  // event loop is enough for the click to have been handled.
  setTimeout(() => URL.revokeObjectURL(url), 0);

  return { filename, rowCount: rowCountHeader ? Number(rowCountHeader) : null };
}

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return String(error);
}
